import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import { useSnackBar } from "~/components/snackbar";
import { Subtitle1 } from "@material/react-typography";
import FormBottomRow from "~/components/form-bottom-row";
import Button from "~/components/button";
import Spinner from "~/components/spinner";
import { useCompleteGroupedFolderMoveMutation } from "../mutations.hotchoc.generated";
import { MutationForm, ErrorRenderer } from "~/components/forms/MutationForm";
import { ValidationResponse } from "~/gql/types";
import { useProjectFoldersPageApi } from "../ProjectFoldersPageApi";
import { CopyFolderPathDisplay } from "../components/CopyFolderPathDisplay";
import { GroupedFolder } from "../types";

type CompleteGroupedFolderMove = {
  folder: GroupedFolder;
};

export const CompleteGroupedFolderMove: React.FC<CompleteGroupedFolderMove> = ({
  folder,
}) => {
  const addAlert = useSnackBar();
  const { reload } = useProjectFoldersPageApi();
  const [openCompleteMove, setOpenCompleteMove] = useState<boolean>(false);

  const [completeGroupedFolderMove, { loading }] =
    useCompleteGroupedFolderMoveMutation();

  const runMutation = async () => {
    const variables = {
      input: {
        folderId: folder.id,
      },
    };

    const result = await completeGroupedFolderMove({ variables });
    return result?.data?.projectFolders
      ?.completeGroupedFolderMove as ValidationResponse;
  };

  const handleSuccess = () => {
    setOpenCompleteMove(false);
    addAlert({
      key: `complete-move${folder.name}`,
      message: `Successfully completed move for ${folder.name}`,
      isSuccess: true,
    });
    reload();
  };

  return (
    <>
      <Button onClick={() => setOpenCompleteMove(true)}>
        Complete Grouped Folder Move
      </Button>
      <Dialog
        open={openCompleteMove}
        onClose={() => setOpenCompleteMove(false)}
      >
        <>
          <Spinner open={loading} />
          <DialogTitle>Complete Grouped Folder Move</DialogTitle>
          <DialogContent>
            <Subtitle1>{`Mark complete once ${folder.name} has been moved to the target folder.`}</Subtitle1>
            <CopyFolderPathDisplay
              currentPath={folder.currentFolderPath}
              targetPath={folder.targetFolderPath!}
            />
            <MutationForm
              onSuccess={handleSuccess}
              runMutation={runMutation}
              initialValues={{ folderId: folder.id }}
              allowPristineSubmit={true}
            >
              <ErrorRenderer
                render={(submitError) => (
                  <FormBottomRow
                    buttonText="Mark complete"
                    errorMessage={submitError}
                    onCancel={() => setOpenCompleteMove(false)}
                  />
                )}
              />
            </MutationForm>
          </DialogContent>
        </>
      </Dialog>
    </>
  );
};
